import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import jsPDF from 'jspdf';
import JsBarcode from 'jsbarcode';
import * as QRCode from 'qrcode';
import { Pallets } from '../interfaces/Pallets.interfaces';
import { Dealer } from '../interfaces/waybill.interfaces';

/**
 * Service per la generazione della label del pallet di reso.
 * Stesso approccio di LabelGenerationService (shipment-request):
 * PDF generato client-side con jsPDF, barcode Code-128 con JsBarcode
 * e QR code, su pagina singola con le dimensioni fisiche dell'etichetta.
 * Layout speculare alla label colli, adattato al reso: il dealer è il
 * mittente e Bertani il destinatario.
 */
@Injectable({
    providedIn: 'root'
})
export class PalletLabelService {
    // Dimensioni standard etichetta in mm (stesse di LabelGenerationService)
    private readonly LABEL_WIDTH = 100; // mm (10cm)
    private readonly LABEL_HEIGHT = 140; // mm (14cm)

    /**
     * Genera e scarica il PDF con la label del pallet di reso.
     * @param pallet Il pallet di cui stampare la label
     * @param dealerInfo Anagrafica del dealer mittente del reso
     * @returns Observable con l'esito della generazione
     */
    generatePalletLabel(pallet: Pallets, dealerInfo?: Dealer | null): Observable<boolean> {
        const generateAsync = async () => {
            try {
                const pdf = new jsPDF({
                    orientation: this.LABEL_WIDTH > this.LABEL_HEIGHT ? 'landscape' : 'portrait',
                    unit: 'mm',
                    format: [this.LABEL_WIDTH, this.LABEL_HEIGHT]
                });

                await this.drawPalletLabelOnPDF(pdf, pallet, dealerInfo, this.LABEL_WIDTH, this.LABEL_HEIGHT);

                const filename = `Label_${pallet.palletCode}_${new Date().toISOString().slice(0, 10)}.pdf`;
                pdf.save(filename);

                return true;
            } catch (error) {
                console.error('Error generating pallet label:', error);
                return false;
            }
        };

        return new Observable(observer => {
            generateAsync().then(result => {
                observer.next(result);
                observer.complete();
            }).catch(error => {
                console.error('Error in async pallet label generation:', error);
                observer.next(false);
                observer.complete();
            });
        });
    }

    /**
     * Genera e scarica un unico PDF con le label di più pallet: una pagina
     * per pallet, stesso layout della label singola. Usato dal popup di
     * riepilogo dopo la creazione di una richiesta con più resi.
     * @param pallets I pallet di cui stampare le label (una pagina ciascuno)
     * @param dealerInfo Anagrafica del dealer mittente del reso
     * @returns Observable con l'esito della generazione
     */
    generatePalletLabels(pallets: Pallets[], dealerInfo?: Dealer | null): Observable<boolean> {
        const generateAsync = async () => {
            try {
                const list = (pallets || []).filter((p) => !!p);
                if (!list.length) {
                    return false;
                }

                const orientation = this.LABEL_WIDTH > this.LABEL_HEIGHT ? 'landscape' : 'portrait';
                const pdf = new jsPDF({
                    orientation,
                    unit: 'mm',
                    format: [this.LABEL_WIDTH, this.LABEL_HEIGHT]
                });

                for (let i = 0; i < list.length; i++) {
                    if (i > 0) {
                        pdf.addPage([this.LABEL_WIDTH, this.LABEL_HEIGHT], orientation);
                    }
                    await this.drawPalletLabelOnPDF(pdf, list[i], dealerInfo, this.LABEL_WIDTH, this.LABEL_HEIGHT);
                }

                const date = new Date().toISOString().slice(0, 10);
                const filename = list.length === 1
                    ? `Label_${list[0].palletCode}_${date}.pdf`
                    : `Labels_${list.length}_resi_${date}.pdf`;
                pdf.save(filename);

                return true;
            } catch (error) {
                console.error('Error generating pallet labels:', error);
                return false;
            }
        };

        return new Observable(observer => {
            generateAsync().then(result => {
                observer.next(result);
                observer.complete();
            }).catch(error => {
                console.error('Error in async pallet labels generation:', error);
                observer.next(false);
                observer.complete();
            });
        });
    }

    /**
     * Disegna la label del pallet sulla pagina PDF.
     */
    private async drawPalletLabelOnPDF(
        pdf: jsPDF,
        pallet: Pallets,
        dealerInfo?: Dealer | null,
        labelWidth: number = this.LABEL_WIDTH,
        labelHeight: number = this.LABEL_HEIGHT
    ): Promise<void> {
        const startX = 3;
        const startY = 3;
        const usableWidth = labelWidth - 6;
        const usableHeight = labelHeight - 6;

        // Bordo etichetta
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(0.3);
        pdf.rect(1, 1, labelWidth - 2, labelHeight - 2);

        // Header azienda (destinatario del reso)
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(16);
        pdf.setTextColor(0, 0, 0);
        pdf.text('BERTANI TRASPORTI S.P.A.', startX, startY + 8, { align: 'left' });

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(6);
        pdf.setTextColor(80, 80, 80);
        pdf.text('Via Cavour, 58, 46043 Castiglione delle Stiviere (Mantova)', startX, startY + 12, { align: 'left' });
        pdf.setTextColor(0, 0, 0);

        // QR code col codice pallet (in alto a destra)
        if (pallet.palletCode) {
            try {
                const qrCodeSize = 15; // mm
                const qrCodeX = startX + usableWidth - qrCodeSize;
                const qrCodeY = startY - 1;
                const qrCodeDataUrl = await this.generateQRCode(pallet.palletCode, 100);
                pdf.addImage(qrCodeDataUrl, 'PNG', qrCodeX, qrCodeY, qrCodeSize, qrCodeSize);
            } catch (error) {
                console.warn('Could not generate QR code for pallet:', pallet.palletCode, error);
            }
        }

        // Divider dopo header
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(0.3);
        pdf.line(startX, startY + 14, startX + usableWidth, startY + 14);

        // Mittente: il dealer che rende la merce
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(7);
        const senderLabel = 'Mittente:';
        pdf.text(senderLabel, startX, startY + 18, { align: 'left' });
        const dealerCode = dealerInfo?.dealerCode || pallet.palletDealerCode;
        if (dealerCode) {
            // Affiancato all'etichetta: posizione calcolata sulla larghezza
            // reale di 'Mittente:' invece che con un offset fisso.
            const codeX = startX + pdf.getTextWidth(senderLabel) + 1.5;
            pdf.text(`[ Cod: ${dealerCode} ]`, codeX, startY + 18, { align: 'left' });
        }

        // Data reso (box nero a destra, come "Consegna Entro" della label colli):
        // usa la data di consegna stimata del pallet.
        if (pallet.palletEstimatedDeliveryDate) {
            const palletDate = new Date(pallet.palletEstimatedDeliveryDate);
            if (!isNaN(palletDate.getTime())) {
                const formattedDate = `${palletDate.getDate().toString().padStart(2, '0')}/` +
                    `${(palletDate.getMonth() + 1).toString().padStart(2, '0')}/` +
                    `${palletDate.getFullYear()}`;
                const dateText = `Data Reso: ${formattedDate}`;
                const boxWidth = 52;
                const boxHeight = 6;
                const boxX = startX + usableWidth - boxWidth;
                const boxY = startY + 14;
                pdf.setFillColor(0, 0, 0);
                pdf.setDrawColor(0, 0, 0);
                pdf.setLineWidth(0.2);
                pdf.rect(boxX, boxY, boxWidth, boxHeight, 'FD');
                pdf.setTextColor(255, 255, 255);
                pdf.setFont('helvetica', 'bold');
                pdf.setFontSize(8);
                pdf.text(dateText, boxX + 2, boxY + 4, { align: 'left' });
                pdf.setTextColor(0, 0, 0);
            }
        }

        // Nome dealer (grassetto, font maggiore)
        const dealerName = dealerInfo?.dealerName || dealerInfo?.dealerLegalName;
        if (dealerName) {
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(10);
            const nameText = dealerName.length > 60 ? dealerName.substring(0, 60) + '...' : dealerName;
            pdf.text(nameText, startX, startY + 26, { align: 'left' });
        }

        // Anagrafica dealer (stesso layout della label colli)
        if (dealerInfo) {
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(9);
            let dealerY = startY + 32;
            const leftColX = startX;
            const rightColX = startX + usableWidth / 2;

            const street = dealerInfo.dealerOperatingAddressStreet || dealerInfo.dealerLegalAddressStreet;
            if (street) {
                const streetText = street.length > 60 ? street.substring(0, 60) + '...' : street;
                pdf.text(streetText, leftColX, dealerY);
            }
            dealerY += 6;

            let cityProvince = dealerInfo.dealerOperatingAddressCity || dealerInfo.dealerLegalAddressCity || '';
            const province = dealerInfo.dealerOperatingAddressProvince || dealerInfo.dealerLegalAddressProvince;
            if (province) {
                cityProvince += ` (${province})`;
            }
            if (cityProvince) {
                const cityProvinceText = cityProvince.length > 25 ? cityProvince.substring(0, 25) + '...' : cityProvince;
                pdf.text(cityProvinceText, leftColX, dealerY);
            }
            dealerY += 6;

            const zip = dealerInfo.dealerOperatingAddressZip || dealerInfo.dealerLegalAddressZip || 'N/A';
            pdf.text(`CAP: ${zip}`, leftColX, dealerY);
            dealerY += 6;

            const vatNumber = dealerInfo.dealerVatNumber || 'N/A';
            pdf.text(`P.IVA: ${vatNumber}`, leftColX, dealerY);
        }

        // Divider dopo anagrafica dealer
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(0.3);
        pdf.line(startX, startY + 56, startX + usableWidth, startY + 56);

        // Parametri reso
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(7);
        pdf.setTextColor(0, 0, 0);
        pdf.text('Parametri Reso:', startX, startY + 60);

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        pdf.text('Bolla:', startX, startY + 68);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(11);
        pdf.text(`${pallet.palletWaybillCode || 'N/A'}`, startX + 13, startY + 68);

        // Tipologia reso / contenitore: box nero a tutta larghezza (come Tour)
        if (pallet.palletDeliveryType) {
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(11);
            const typeText = pallet.palletDeliveryType.length > 45
                ? pallet.palletDeliveryType.substring(0, 45) + '...'
                : pallet.palletDeliveryType;
            const boxWidth = usableWidth;
            const boxHeight = 6;
            const boxX = startX;
            const boxY = startY + 74;
            pdf.setFillColor(0, 0, 0);
            pdf.setDrawColor(0, 0, 0);
            pdf.setLineWidth(0.2);
            pdf.rect(boxX, boxY, boxWidth, boxHeight, 'FD');
            pdf.setTextColor(255, 255, 255);
            pdf.text(typeText, boxX + boxWidth / 2, boxY + boxHeight / 2 + 1.5, { align: 'center' });
            pdf.setTextColor(0, 0, 0);
        }

        // Divider dopo parametri reso
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(0.3);
        pdf.line(startX, startY + 82, startX + usableWidth, startY + 82);

        // Parametri pallet
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(7);
        pdf.text('Parametri Pallet:', startX, startY + 86);

        let paramY = startY + 92;

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        pdf.text('Peso:', startX, paramY);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(11);
        pdf.text(
            pallet.palletWeight != null && pallet.palletWeight !== 0
                ? `${pallet.palletWeight} kg`
                : '-',
            startX + 18,
            paramY
        );
        paramY += 6;

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        pdf.text('Volume:', startX, paramY);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(11);
        pdf.text(
            pallet.palletVolume != null && pallet.palletVolume !== 0
                ? `${pallet.palletVolume} m³`
                : '-',
            startX + 18,
            paramY
        );
        paramY += 6;

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        pdf.text('Note:', startX, paramY);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(11);
        if (pallet.palletNote && pallet.palletNote.trim().length > 0) {
            const note = pallet.palletNote.length > 80
                ? pallet.palletNote.substring(0, 80)
                : pallet.palletNote;
            const lines = (pdf.splitTextToSize(note, usableWidth - 20) as string[]).slice(0, 3);
            lines.forEach((line, idx) => pdf.text(line, startX + 18, paramY + idx * 5));
        } else {
            pdf.text('-', startX + 18, paramY);
        }

        // Barcode del codice pallet in fondo alla label
        pdf.setTextColor(0, 0, 0);
        if (pallet.palletCode) {
            const barcodeWidth = usableWidth * 0.8;
            const barcodeX = startX + (usableWidth - barcodeWidth) / 2;
            this.drawSimpleBarcodeOnPDF(pdf, pallet.palletCode, barcodeX, startY + usableHeight - 18, barcodeWidth, 12);
        }

        pdf.setTextColor(0, 0, 0);
    }

    /**
     * Disegna un barcode Code-128 con JsBarcode (stesso metodo di LabelGenerationService).
     */
    private drawSimpleBarcodeOnPDF(
        pdf: jsPDF,
        code: string,
        x: number,
        y: number,
        width: number,
        height: number
    ): void {
        try {
            const canvas = document.createElement('canvas');
            JsBarcode(canvas, code, {
                format: "CODE128",
                width: 2,
                height: 100,
                displayValue: false,
                margin: 0,
                background: "#ffffff",
                lineColor: "#000000"
            });

            const barcodeDataUrl = canvas.toDataURL('image/png');
            pdf.addImage(barcodeDataUrl, 'PNG', x, y, width, height);

            pdf.setFont('courier', 'bold');
            pdf.setFontSize(14);
            pdf.text(code, x + width / 2, y + height + 4, { align: 'center' });
        } catch (error) {
            console.error('Error generating barcode with JsBarcode:', error);
            this.drawFallbackBarcode(pdf, code, x, y, width, height);
        }
    }

    /**
     * Fallback se JsBarcode fallisce: rettangolo con testo.
     */
    private drawFallbackBarcode(
        pdf: jsPDF,
        code: string,
        x: number,
        y: number,
        width: number,
        height: number
    ): void {
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(0.2);
        pdf.rect(x, y, width, height);
        pdf.setFont('courier', 'bold');
        pdf.setFontSize(6);
        pdf.text('BARCODE', x + width / 2, y + height / 2, { align: 'center' });
        pdf.text(code, x + width / 2, y + height + 6, { align: 'center' });
    }

    /**
     * Genera il QR code del codice pallet come data URL.
     */
    private async generateQRCode(code: string, size: number = 128): Promise<string> {
        try {
            return await QRCode.toDataURL(code, {
                width: size,
                margin: 1,
                color: {
                    dark: '#000000',
                    light: '#FFFFFF'
                },
                errorCorrectionLevel: 'M'
            });
        } catch (error) {
            console.error('Error generating QR code:', error);
            return this.generateFallbackQRCode(size);
        }
    }

    /**
     * Placeholder di fallback se la generazione del QR code fallisce.
     */
    private generateFallbackQRCode(size: number): string {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');

        if (ctx) {
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, size, size);
            ctx.strokeStyle = '#000000';
            ctx.lineWidth = 2;
            ctx.strokeRect(2, 2, size - 4, size - 4);
            ctx.fillStyle = '#000000';
            ctx.font = 'bold 16px Arial';
            ctx.textAlign = 'center';
            ctx.fillText('QR', size / 2, size / 2 + 6);
        }

        return canvas.toDataURL('image/png');
    }
}
